import type { TerminalResource } from '@repo/api-client';

/**
 * What a terminal row actually is, and what that is called on screen.
 *
 * The ticket's central point: **a terminal created in the back office is not a
 * device.** It is a row waiting to be registered, and registration (FE011)
 * happens at the counter on the machine itself. A list that showed every row the
 * same way would have somebody carry an unregistered prefix to a gate and
 * discover it there, mid-queue.
 *
 * The API says which is which without a field for it. `status` separates live
 * from withdrawn; `publicKeyId` is documented as "the signing key that issued
 * its current lease, or **null while the device has never been put into
 * service**", which is precisely the distinction this screen has to draw. Three
 * states derived from two fields, in one place, rather than a ternary in a cell.
 */
export type TerminalState = 'IN_SERVICE' | 'AWAITING_REGISTRATION' | 'REVOKED';

/**
 * Whether a terminal has ever been registered.
 *
 * `publicKeyId` is a real `string | null` as of the 10 Aug 2026 regeneration —
 * the annotation gap this was written around (`#209`) is closed. It stays a
 * presence check because that is the question `TerminalState` asks; the id
 * itself is a signing key and not a thing to put in front of anybody.
 */
const hasBeenRegistered = (terminal: TerminalResource): boolean =>
  terminal.publicKeyId !== null && terminal.publicKeyId !== undefined;

export function stateOf(terminal: TerminalResource): TerminalState {
  // Withdrawn first and unconditionally. A revoked device may well still carry
  // the key that signed the lease it is no longer allowed to use, and "in
  // service" is the one thing it must never be called.
  if (terminal.status === 'REVOKED') return 'REVOKED';

  return hasBeenRegistered(terminal) ? 'IN_SERVICE' : 'AWAITING_REGISTRATION';
}

/**
 * `Record` rather than a function with a default, so exhaustiveness is the type
 * system's problem and not a runtime fallback nobody ever sees.
 *
 * "Not yet registered" and not "Pending" or "Inactive": it names the step that
 * has not happened, which is the only wording that tells the reader what to do
 * about it.
 */
const STATE_LABELS: Record<TerminalState, string> = {
  IN_SERVICE: 'In service',
  AWAITING_REGISTRATION: 'Not yet registered',
  REVOKED: 'Revoked',
};

export const labelForState = (state: TerminalState): string => STATE_LABELS[state];
